import mapboxgl from 'mapbox-gl';
import { hexToRgbArray } from '@shared/helpers/color.helpers';
import { canvasColors, mapRenderingConstants } from '../map.constants';
import { MapStoreyProperty } from '../models/map-feature.models';

type PointFeatureProperties = {
  [MapStoreyProperty.Selected]?: boolean;
  [MapStoreyProperty.Hover]?: boolean;
  [MapStoreyProperty.Elevation]?: number;
};

type PointFeature = GeoJSON.Feature<GeoJSON.Point, PointFeatureProperties>;

type PointsVertex = {
  x: number;
  y: number;
  z: number;
  cx: number;
  cy: number;
  fr: number;
  fg: number;
  fb: number;
  r: number;
  g: number;
  b: number;
  opacity: number;
  size: number; // diameter in px
  /** Stroke width scale (1 = base; e.g. 3 for selected point ring). */
  strokeScale: number;
};

export function buildPointVertices({
  features,
  originMercator,
  penMode = false,
}: {
  features: PointFeature[];
  originMercator: mapboxgl.MercatorCoordinate;
  /** Pen-tool draft vertices: 15px, white fill, 1px blue border (DEV-2576). */
  penMode?: boolean;
}): { vertices: Float32Array; vertexCount: number } {
  if (!features.length) return { vertices: new Float32Array(), vertexCount: 0 };

  // Henrik: Use the exact same blue as the selected line for fill when selected.
  const blue = hexToRgbArray(canvasColors.lineBlue);
  const white = [1, 1, 1] as const;

  const vertices: PointsVertex[] = [];

  for (const feature of features) {
    const coords = feature.geometry?.coordinates;
    if (!coords || coords.length < 2) continue;
    const lng = coords[0];
    const lat = coords[1];
    if (typeof lng !== 'number' || typeof lat !== 'number') continue;

    const props = feature.properties ?? {};
    const selected = props[MapStoreyProperty.Selected] === true;
    const hover = props[MapStoreyProperty.Hover] === true;
    // Henrik: keep points fully opaque; varying opacity makes the stroke look "brighter" due to blending.
    const opacity = 1;

    const elevation =
      typeof props[MapStoreyProperty.Elevation] === 'number'
        ? props[MapStoreyProperty.Elevation]
        : 0;

    const merc = mapboxgl.MercatorCoordinate.fromLngLat(
      { lng, lat },
      elevation,
    );

    const x = merc.x - originMercator.x;
    const y = merc.y - originMercator.y;
    const z = merc.z - originMercator.z;

    // Hover grows both default and selected points by the same 2px increment.
    // Selected points keep their inverted colors (blue fill, white stroke).
    // Insert-candidate mid-edge dots use the same styling; Hover grows them when active.
    // Pen draft points stay 15px with a 1px blue border (white fill).
    let fill: readonly [number, number, number] = white;
    let stroke: readonly [number, number, number] = blue;
    let size = mapRenderingConstants.pointSizePx;
    let strokeScale = 1;
    if (penMode) {
      size = mapRenderingConstants.penPointSizePx;
      strokeScale = 1 / mapRenderingConstants.lineWidthPx;
    } else if (selected) {
      fill = blue;
      stroke = white;
      size = hover
        ? mapRenderingConstants.pointSizeSelectedHoverPx
        : mapRenderingConstants.pointSizeHoverPx;
      strokeScale = mapRenderingConstants.pointStrokeScaleSelected;
    } else if (hover) {
      size = mapRenderingConstants.pointSizeHoverPx;
    }
    const [fr, fg, fb] = fill;
    const [r, g, b] = stroke;

    pushPointQuad(vertices, {
      x,
      y,
      z,
      fr,
      fg,
      fb,
      r,
      g,
      b,
      opacity,
      size,
      strokeScale,
    });
  }

  return packPointVertices(vertices);
}

function pushPointQuad(
  out: PointsVertex[],
  center: {
    x: number;
    y: number;
    z: number;
    fr: number;
    fg: number;
    fb: number;
    r: number;
    g: number;
    b: number;
    opacity: number;
    size: number;
    strokeScale: number;
  },
): void {
  const { x, y, z, fr, fg, fb, r, g, b, opacity, size, strokeScale } = center;

  const add = (cx: number, cy: number): void => {
    out.push({
      x,
      y,
      z,
      cx,
      cy,
      fr,
      fg,
      fb,
      r,
      g,
      b,
      opacity,
      size,
      strokeScale,
    });
  };

  // Tri 1
  add(-1, -1);
  add(1, -1);
  add(1, 1);

  // Tri 2
  add(-1, -1);
  add(1, 1);
  add(-1, 1);
}

function packPointVertices(vertices: PointsVertex[]): {
  vertices: Float32Array;
  vertexCount: number;
} {
  const vertexCount = vertices.length;
  if (vertexCount <= 0) return { vertices: new Float32Array(), vertexCount: 0 };

  // Layout: pos(3), corner(2), fill(3), stroke(3), opacity(1), size(1), strokeScale(1) = 14 floats
  const out = new Float32Array(vertexCount * 14);

  for (let i = 0; i < vertexCount; i += 1) {
    const v = vertices[i];
    if (!v) continue;

    const o = i * 14;
    out[o] = v.x;
    out[o + 1] = v.y;
    out[o + 2] = v.z;
    out[o + 3] = v.cx;
    out[o + 4] = v.cy;
    out[o + 5] = v.fr;
    out[o + 6] = v.fg;
    out[o + 7] = v.fb;
    out[o + 8] = v.r;
    out[o + 9] = v.g;
    out[o + 10] = v.b;
    out[o + 11] = v.opacity;
    out[o + 12] = v.size;
    out[o + 13] = v.strokeScale;
  }

  return { vertices: out, vertexCount };
}
